import React, { useState, useEffect } from 'react';

interface Message {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
    suggestions?: string[];
}

interface AIAgentProps {
    context?: 'dashboard' | 'analysis' | 'calculator' | 'tools' | 'general';
    buildingData?: any;
    currentPage?: string;
}

const AIAgent: React.FC<AIAgentProps> = ({ 
    context = 'general', 
    buildingData, 
    currentPage 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    // Initialize with context-specific welcome message
    useEffect(() => {
        const welcomeMessage = getWelcomeMessage();
        setMessages([{
            id: '1',
            text: welcomeMessage.text,
            isUser: false,
            timestamp: new Date(),
            suggestions: welcomeMessage.suggestions
        }]);
    }, [context, currentPage]);

    const getWelcomeMessage = () => {
        switch (context) {
            case 'dashboard':
                return {
                    text: `Hi! I'm your NYC Energy Retrofit assistant. I can help you:\n\n• Understand your building's energy performance\n• Navigate LL97 compliance requirements\n• Calculate potential savings and ROI\n• Find qualified contractors and financing\n• Identify available incentives and rebates\n\nWhat would you like to explore today?`,
                    suggestions: [
                        'Explain my building analysis',
                        'LL97 compliance deadline',
                        'Find retrofit contractors',
                        'Calculate energy savings'
                    ]
                };
            case 'analysis':
                return {
                    text: `I can help interpret your building analysis results and guide you through next steps:\n\n• Energy performance metrics\n• LL97 compliance gaps\n• Retrofit recommendations\n• Cost-benefit analysis\n• Implementation timeline\n\nWhat aspect would you like me to explain?`,
                    suggestions: [
                        'Explain my energy score',
                        'Show compliance gaps',
                        'Recommend improvements',
                        'Calculate payback period'
                    ]
                };
            case 'calculator':
                return {
                    text: `I'm here to help with energy calculations and projections:\n\n• LL97 penalty calculations\n• Energy savings estimates\n• ROI and payback analysis\n• Utility cost projections\n• Retrofit cost estimates\n\nWhat would you like me to calculate?`,
                    suggestions: [
                        'Calculate LL97 penalties',
                        'Estimate energy savings',
                        'ROI for HVAC upgrade',
                        'Compare retrofit options'
                    ]
                };
            case 'tools':
                return {
                    text: `Welcome to the NYC Energy Tools! I can guide you through:\n\n• Building analysis tools\n• LL97 compliance calculator\n• Target property analyzer\n• Contractor connection platform\n• Financial planning tools\n\nWhich tool would you like help with?`,
                    suggestions: [
                        'How to analyze my building',
                        'Use LL97 calculator',
                        'Find target properties',
                        'Connect with contractors'
                    ]
                };
            default:
                return {
                    text: `Welcome to the NYC Energy Retrofit Platform! 🏢⚡\n\nI'm your AI assistant for navigating Local Law 97 compliance and energy retrofits. I can help with:\n\n• Building energy analysis and benchmarking\n• LL97 compliance requirements and deadlines\n• Contractor recommendations and vetting\n• Financing options and incentive programs\n• ROI calculations and project planning\n\nHow can I help you get started with your energy retrofit project?`,
                    suggestions: [
                        'Analyze my building for LL97',
                        'Find qualified contractors',
                        'Calculate potential savings',
                        'Explore financing options'
                    ]
                };
        }
    };

    const generateResponse = async (userMessage: string): Promise<Message> => {
        // Simulate AI processing delay
        await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
        
        const lowerMessage = userMessage.toLowerCase();
        let response = '';
        let suggestions: string[] = [];

        // Context-aware responses
        if (lowerMessage.includes('ll97') || lowerMessage.includes('compliance')) {
            response = `📋 **Local Law 97 Quick Guide**\n\n**Current Status (2024-2029):**\n• Buildings >25k sq ft must meet emissions limits\n• Baseline: 2005-2014 average building performance\n• Penalties: $268/metric ton CO2e over limit\n\n**Upcoming Changes (2030+):**\n• 40% reduction in emissions limits\n• Stricter penalties and enforcement\n• More building types included\n\n**Key Compliance Strategies:**\n1. Energy audits and benchmarking\n2. HVAC system upgrades\n3. Building envelope improvements\n4. Renewable energy integration\n\nWould you like specific guidance for your building type?`;
            suggestions = ['Calculate my penalties', 'Find compliance contractors', 'Analyze my building', 'Show retrofit options'];
        } else if (lowerMessage.includes('contractor') || lowerMessage.includes('find')) {
            response = `🔧 **NYC Energy Contractor Network**\n\n**Top Rated Contractors:**\n\n**EcoRetrofit NYC** ⭐⭐⭐⭐⭐\n• Specializes in LL97 compliance\n• Avg project: $500K - $2M\n• NYSERDA certified partner\n\n**GreenBuild Solutions** ⭐⭐⭐⭐⭐\n• Commercial HVAC specialists\n• Fast project turnaround\n• Financing partnerships available\n\n**Sustainable Systems Pro** ⭐⭐⭐⭐\n• Building envelope experts\n• Large project experience\n• CPACE financing certified\n\nWould you like me to help you connect with contractors based on your specific needs?`;
            suggestions = ['Get contractor quotes', 'Compare specialties', 'Check availability', 'See project examples'];
        } else if (lowerMessage.includes('saving') || lowerMessage.includes('cost') || lowerMessage.includes('calculate')) {
            response = `💰 **Energy Savings Calculator**\n\n**Typical Retrofit Savings:**\n\n**HVAC Upgrade (Variable refrigerant flow):**\n• Energy savings: 25-40%\n• Annual cost reduction: $15-25/sq ft\n• Payback period: 7-12 years\n• LL97 penalty avoidance: $5-15/sq ft/year\n\n**Building Envelope (windows + insulation):**\n• Energy savings: 15-25%\n• Annual cost reduction: $8-15/sq ft\n• Payback period: 10-18 years\n\n**Combined Package:**\n• Energy savings: 35-50%\n• Total annual savings: $20-35/sq ft\n• LL97 compliance achieved\n\nWant me to calculate specific savings for your building?`;
            suggestions = ['Calculate for my building', 'Compare retrofit options', 'Show financing options', 'Estimate payback period'];
        } else if (lowerMessage.includes('financing') || lowerMessage.includes('loan') || lowerMessage.includes('cpace')) {
            response = `🏦 **Financing Your Energy Retrofit**\n\n**Best Options for Energy Projects:**\n\n**NYC CPACE Program** (Recommended)\n• Rates: 3.5-5.5% fixed\n• Terms: Up to 30 years\n• No personal guarantees\n• Tax benefits available\n• Transferable to new owner\n\n**Traditional Commercial Loans**\n• Rates: 4.5-8.5%\n• Terms: 5-20 years\n• Faster closing process\n• Good for smaller projects\n\n**Equipment Financing**\n• Rates: 5-12%\n• Terms: 5-15 years\n• Equipment as collateral\n• Quick approval process\n\nWhich financing option interests you most?`;
            suggestions = ['Learn about CPACE', 'Compare loan options', 'Check qualification requirements', 'Get financing quotes'];
        } else if (lowerMessage.includes('incentive') || lowerMessage.includes('rebate') || lowerMessage.includes('program')) {
            response = `🎁 **Available Incentive Programs**\n\n**NYSERDA Programs:**\n• FlexTech Studies: Up to $100K for feasibility studies\n• Commercial Incentives: $0.05-0.15/kWh saved\n• Heat Pump Programs: Up to $1M in incentives\n\n**Utility Programs (Con Ed/National Grid):**\n• HVAC Rebates: $500-5,000 per unit\n• Lighting Upgrades: $25-75 per fixture\n• Custom Incentives: Up to $1M for large projects\n\n**NYC Programs:**\n• Property Tax Abatements: Up to 20% reduction\n• Accelerated Depreciation: MACRS benefits\n• Green Building Credits: Additional property value\n\nWant help applying for specific programs?`;
            suggestions = ['Find programs for my project', 'Check eligibility requirements', 'Calculate total incentives', 'Get application help'];
        } else if (lowerMessage.includes('building') || lowerMessage.includes('analyze') || lowerMessage.includes('analysis')) {
            response = `🏢 **Building Analysis Overview**\n\nOur platform analyzes your building across multiple dimensions:\n\n**Energy Performance:**\n• Historical utility usage patterns\n• Benchmark comparison to similar buildings\n• Seasonal variations and peak demand\n\n**LL97 Compliance:**\n• Current emissions vs. limits\n• Future compliance projections\n• Penalty risk assessment\n\n**Retrofit Opportunities:**\n• HVAC system efficiency\n• Building envelope performance\n• Lighting and control systems\n• Renewable energy potential\n\nWould you like me to walk you through analyzing your building?`;
            suggestions = ['Start building analysis', 'Upload utility bills', 'Check LL97 status', 'See retrofit recommendations'];
        } else {
            response = `I'm here to help with your NYC energy retrofit journey! I can assist with:\n\n🏢 **Building Analysis** - Understand your property's energy performance\n📋 **LL97 Compliance** - Navigate regulations and avoid penalties\n🔧 **Contractor Network** - Connect with qualified professionals\n💰 **Financial Planning** - Explore financing and incentive options\n📊 **ROI Calculations** - Evaluate project economics\n\nWhat specific area would you like to explore?`;
            suggestions = ['Start with building analysis', 'Learn about LL97 requirements', 'Find qualified contractors', 'Explore financing options'];
        }

        return {
            id: Date.now().toString(),
            text: response,
            isUser: false,
            timestamp: new Date(),
            suggestions
        };
    };

    const handleSendMessage = async (messageText?: string) => {
        const text = messageText || inputText.trim();
        if (!text) return;

        // Add user message
        const userMessage: Message = {
            id: Date.now().toString(),
            text,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsTyping(true);

        // Generate AI response
        const aiResponse = await generateResponse(text);
        setIsTyping(false);
        setMessages(prev => [...prev, aiResponse]);
    };

    const handleSuggestionClick = (suggestion: string) => {
        handleSendMessage(suggestion);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <>
            {/* AI Agent Toggle Button */}
            <div className="fixed bottom-6 right-6 z-50">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
                        isOpen 
                            ? 'bg-red-500 hover:bg-red-600' 
                            : 'bg-emerald-600 hover:bg-emerald-700'
                    } text-white`}
                >
                    {isOpen ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                    )}
                </button>
                
                {/* Notification indicator for new AI assistant */}
                {!isOpen && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
                        <span className="text-xs text-white font-bold">AI</span>
                    </div>
                )}
            </div>

            {/* AI Agent Chat Interface */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 w-96 h-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 flex flex-col">
                    {/* Header */}
                    <div className="bg-emerald-600 text-white p-4 rounded-t-lg">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                                🤖
                            </div>
                            <div>
                                <h3 className="font-semibold">NYC Retrofit AI</h3>
                                <p className="text-xs text-emerald-100">Your LL97 compliance assistant</p>
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((message) => (
                            <div key={message.id} className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                    message.isUser 
                                        ? 'bg-emerald-600 text-white' 
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    <p className="text-sm whitespace-pre-line">{message.text}</p>
                                    <p className="text-xs mt-1 opacity-70">
                                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}

                        {/* Typing indicator */}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-gray-100 px-4 py-2 rounded-lg">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Suggestions */}
                        {messages.length > 0 && messages[messages.length - 1].suggestions && !isTyping && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-500">Quick actions:</p>
                                {messages[messages.length - 1].suggestions!.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="block w-full text-left text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-2 rounded border border-emerald-200 transition-colors"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <div className="border-t border-gray-200 p-4">
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask about LL97, retrofits, contractors..."
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <button
                                onClick={() => handleSendMessage()}
                                disabled={!inputText.trim()}
                                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AIAgent;
